import React, { useState } from "react";
import { Mail, Phone, MapPin, Clock, Send, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/messages", form);
      setSuccess(true);
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Hiba történt.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="kapcsolat"
      className="relative py-24 sm:py-32 border-t border-white/5"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <span className="section-eyebrow mb-4">04 / Kapcsolat</span>
          <h2
            className="mt-5 font-black uppercase text-white leading-[0.95]"
            style={{
              fontFamily: "Barlow Condensed",
              fontSize: "clamp(2.25rem, 5vw, 4rem)",
            }}
          >
            Lépjen velünk
            <br />
            <span className="text-[#0082D2]">kapcsolatba.</span>
          </h2>
          <p className="mt-6 text-zinc-400 leading-relaxed max-w-md">
            Bármilyen kérdés esetén forduljon hozzánk bizalommal — válaszolunk
            telefonon vagy e-mailben.
          </p>

          <p
            className="mt-4 text-zinc-500 text-sm leading-relaxed max-w-md"
            data-testid="contact-location-seo"
          >
            Autószervízünk Debrecenben, a Kishegyesi út és a Kishatár úti
            Auchan áruház közvetlen közelében, a Vadvirág utcában található,
            így a nyugati ipari övezetből és a Határ Úti Ipari Parkból is
            percek alatt elérhető.
          </p>

          <div className="mt-10 space-y-5">
            <Info
              icon={Mail}
              label="E-mail"
              value="autoszerelem@gmail.com"
              href="mailto:autoszerelem@gmail.com"
              testId="contact-info-email"
            />
            <Info
              icon={Phone}
              label="Telefon"
              value="+36 30 863 7476"
              href="tel:+36308637476"
              testId="contact-info-phone"
            />
            <Info
              icon={MapPin}
              label="Cím"
              value="4031 Debrecen, Vadvirág utca 15/D — 3. kapu"
              testId="contact-info-address"
            />
            <Info
              icon={Clock}
              label="Nyitvatartás"
              value="Hétfő – Péntek: 08:00 – 17:00"
              testId="contact-info-hours"
            />
          </div>

          <div
            className="mt-8 border border-white/5 overflow-hidden"
            data-testid="contact-map"
          >
            <iframe
              title="Autó-Szerelem — Séra Autószervíz, Debrecen térkép"
              src="https://www.google.com/maps?q=4031+Debrecen,+Vadvir%C3%A1g+utca+15%2FD&output=embed"
              width="100%"
              height="260"
              style={{ border: 0, filter: "grayscale(0.4) invert(0.92) hue-rotate(180deg)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>

        <div className="lg:col-span-7">
          {success ? (
            <div
              className="bg-[#101010] border border-white/5 p-10 text-center"
              data-testid="contact-success"
            >
              <CheckCircle2
                className="w-14 h-14 text-[#22C55E] mx-auto mb-4"
                strokeWidth={1.5}
              />
              <h3
                className="text-2xl font-bold uppercase text-white"
                style={{ fontFamily: "Barlow Condensed" }}
              >
                Köszönjük üzenetét!
              </h3>
              <p className="mt-3 text-zinc-400">
                Hamarosan válaszolunk az Ön által megadott e-mail címre.
              </p>
              <button
                onClick={() => setSuccess(false)}
                className="btn-outline mt-6"
                data-testid="contact-new-btn"
              >
                Új üzenet
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="bg-[#101010] border border-white/5 p-8 sm:p-10 space-y-5"
              data-testid="contact-form"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <label className="block">
                  <span className="label-text">
                    Teljes név <span className="text-[#0082D2]">*</span>
                  </span>
                  <input
                    required
                    type="text"
                    className="input-field"
                    value={form.name}
                    onChange={change("name")}
                    data-testid="contact-name"
                  />
                </label>
                <label className="block">
                  <span className="label-text">
                    E-mail cím <span className="text-[#0082D2]">*</span>
                  </span>
                  <input
                    required
                    type="email"
                    className="input-field"
                    value={form.email}
                    onChange={change("email")}
                    data-testid="contact-email"
                  />
                </label>
              </div>
              <label className="block">
                <span className="label-text">
                  Üzenet <span className="text-[#0082D2]">*</span>
                </span>
                <textarea
                  required
                  className="input-field"
                  rows={6}
                  value={form.message}
                  onChange={change("message")}
                  placeholder="Miben segíthetünk?"
                  data-testid="contact-message"
                />
              </label>

              {error && (
                <div
                  className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 px-4 py-3"
                  data-testid="contact-error"
                >
                  {error}
                </div>
              )}

              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary disabled:opacity-50"
                  data-testid="contact-submit-btn"
                >
                  <Send className="w-4 h-4 mr-2" strokeWidth={2} />
                  {loading ? "Küldés..." : "Üzenet küldése"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Info({ icon: Icon, label, value, href, testId }) {
  const content = (
    <>
      <div className="w-11 h-11 flex items-center justify-center bg-[#0082D2]/10 border border-[#0082D2]/30 shrink-0">
        <Icon className="w-5 h-5 text-[#0082D2]" strokeWidth={1.6} />
      </div>
      <div>
        <div
          className="text-[0.7rem] uppercase tracking-[0.2em] text-zinc-500"
          style={{ fontFamily: "Barlow Condensed" }}
        >
          {label}
        </div>
        <div className="text-base text-white font-medium mt-1">{value}</div>
      </div>
    </>
  );
  return href ? (
    <a
      href={href}
      data-testid={testId}
      className="flex items-center gap-4 hover:text-[#0082D2] transition-colors"
    >
      {content}
    </a>
  ) : (
    <div data-testid={testId} className="flex items-center gap-4">
      {content}
    </div>
  );
}
